/**
 * /accept-invite?token=..., invitation acceptance landing.
 *
 * Three-stage UI rendered inside Foundation's AuthLayout:
 *   1. GET /api/accept-invite?token=... to preview the invitation
 *   2. Render sign-in form (if email already has Olytic account) OR
 *      sign-up form (if new user)
 *   3. Submit POST → set session cookie → redirect to /home
 */

import { AuthLayout, Button, PasswordInput } from '@olytic/foundation';
import { validatePassword } from '@olytic/shared';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authFrame } from '../components/authFrame.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { ApiError } from '../lib/api.ts';
import { RouterNavLink } from '../lib/foundationLinks.tsx';
import type { CurrentUser } from '../lib/types.ts';

interface InvitePreview {
  status: 'pending' | 'accepted';
  account_id?: string;
  account_name: string;
  email: string;
  invited_role?: 'owner' | 'admin' | 'user';
  has_existing_account?: boolean;
}

export function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh, hydrate } = useSession();
  const token = searchParams.get('token') ?? '';

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (token === '') {
      setLoadError('No invitation token in the URL.');
      return;
    }
    let cancelled = false;
    fetch(`/api/accept-invite?token=${encodeURIComponent(token)}`, {
      credentials: 'include',
    })
      .then(async (res) => {
        if (cancelled) return;
        const body = (await res.json()) as InvitePreview | { error: string; message?: string };
        if (!res.ok) {
          const msg = (() => {
            if ('error' in body) {
              if (body.error === 'invitation_expired')
                return 'This invitation expired. Ask your admin to send a fresh one.';
              if (body.error === 'invitation_revoked')
                return 'This invitation was revoked. Ask your admin to send a fresh one.';
              if (body.error === 'not_found')
                return "We couldn't find this invitation. The link may have been mistyped.";
              return body.message ?? body.error;
            }
            return 'Could not load this invitation.';
          })();
          setLoadError(msg);
          return;
        }
        setPreview(body as InvitePreview);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not reach the server.');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loadError !== null) {
    return (
      <AuthLayout
        {...authFrame}
        title="Invitation problem"
        description={loadError}
        footer={
          <Link to="/sign-in" style={{ color: 'var(--olytic-obsidian)', fontWeight: 500 }}>
            Back to sign in
          </Link>
        }
      >
        <Button asChild variant="outline" style={{ width: '100%' }}>
          <RouterNavLink href="/sign-in">Go to sign in</RouterNavLink>
        </Button>
      </AuthLayout>
    );
  }

  if (preview === null) {
    return (
      <AuthLayout {...authFrame} title="Loading invitation…" description="One moment.">
        <div className="loading">
          <span className="spinner" />
        </div>
      </AuthLayout>
    );
  }

  if (preview.status === 'accepted') {
    return (
      <AuthLayout
        {...authFrame}
        title="Already accepted"
        description={`This invitation was already used. Sign in to ${preview.account_name}.`}
      >
        <Button asChild variant="brand" style={{ width: '100%' }}>
          <RouterNavLink href="/sign-in">Go to sign in</RouterNavLink>
        </Button>
      </AuthLayout>
    );
  }

  const mode: 'signin' | 'signup' = preview.has_existing_account === true ? 'signin' : 'signup';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (mode === 'signup' && name.trim() === '') {
      setFormError('Please enter your name.');
      return;
    }
    // Sub-agent PRE-LAUNCH-FIX FIX 4 — shared policy validator.
    // Only enforce for signup mode; signin mode passes the caller's
    // existing password verbatim (they set it under whatever policy
    // was in force at signup time).
    if (mode === 'signup') {
      const pwCheck = validatePassword(password);
      if (!pwCheck.ok) {
        setFormError(pwCheck.error ?? 'Password does not meet the policy.');
        return;
      }
    } else if (password.length === 0) {
      setFormError('Password is required.');
      return;
    }
    if (mode === 'signup' && password !== confirm) {
      setFormError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/accept-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token,
          mode,
          password,
          ...(mode === 'signup' ? { name: name.trim() } : {}),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        redirect_to?: string;
        message?: string;
        error?: string;
        user?: CurrentUser;
      };
      if (!res.ok) {
        throw new ApiError(
          res.status,
          data.message ?? data.error ?? 'Could not accept invitation.',
        );
      }
      // Push #88 (401 race fix, Option B): hydrate session state
      // directly from the response body. /home mounts with an already-
      // authenticated context — no /api/me fetch on first load, no
      // cookie-persistence race. Falls back to refresh() if the server
      // didn't return a user payload (defensive — shouldn't happen
      // post-Push-#88).
      if (data.user !== undefined) {
        hydrate(data.user);
      } else {
        refresh();
      }
      navigate(data.redirect_to ?? '/home');
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not accept invitation.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      {...authFrame}
      title={mode === 'signin' ? 'Sign in to accept' : 'Create your account'}
      description={
        mode === 'signin'
          ? `Signing in adds ${preview.account_name} to your workspaces as ${preview.invited_role}.`
          : `You'll join ${preview.account_name} as ${preview.invited_role}.`
      }
    >
      {/* AuthLayout has no eyebrow slot (AuthShell had one above the title),
         so the invitation line leads the page body. */}
      <div
        style={{
          color: 'var(--olytic-teal)',
          fontSize: '12px',
          fontWeight: 600,
          letterSpacing: '0.02em',
          marginBottom: '8px',
        }}
      >
        Invited to {preview.account_name}
      </div>
      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <div className="form-row">
          <label className="form-label">Email</label>
          <input type="email" value={preview.email} readOnly className="input" />
        </div>

        {mode === 'signup' && (
          <div className="form-row">
            <label className="form-label" htmlFor="accept-name">
              Your name
            </label>
            <input
              id="accept-name"
              type="text"
              required
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              // Round 6 Item 19 — focus the name field on mount when this is
              // a fresh signup; signin mode focuses the password field below.
              autoFocus
            />
          </div>
        )}

        <div className="form-row">
          <label className="form-label" htmlFor="accept-password">
            {mode === 'signin' ? 'Password' : 'Create a password'}
          </label>
          <PasswordInput
            id="accept-password"
            placeholder=""
            value={password}
            onChange={setPassword}
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            // Signin mode = focus password (email is fixed from the invite).
            autoFocus={mode === 'signin'}
          />
        </div>

        {mode === 'signup' && (
          <div className="form-row">
            <label className="form-label" htmlFor="accept-confirm">
              Confirm password
            </label>
            <PasswordInput
              id="accept-confirm"
              placeholder=""
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
            />
          </div>
        )}

        {formError !== null && (
          <div className="callout callout-danger">
            <p className="callout-body">{formError}</p>
          </div>
        )}

        <Button type="submit" variant="brand" disabled={submitting} style={{ width: '100%' }}>
          {submitting ? (
            <span className="loading-inline">
              <span className="spinner" /> Working…
            </span>
          ) : mode === 'signin' ? (
            'Sign in & join'
          ) : (
            'Create account & join'
          )}
        </Button>

        {mode === 'signin' && (
          <p className="muted" style={{ marginTop: '4px', fontSize: '13px', textAlign: 'center' }}>
            Forgot your password?{' '}
            <Link to="/forgot-password" style={{ color: 'var(--olytic-teal)' }}>
              Reset it
            </Link>{' '}
            then come back to this link.
          </p>
        )}
      </form>
    </AuthLayout>
  );
}
